import React, { useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical, Edit, Save, Plus, Trash2 } from 'lucide-react';

function SortableLinkItem({ id, link, onDelete, onRename, onToggleActive, onToggleRequired }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(link.link_label);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleSaveEdit = () => {
        if (editValue.trim() && editValue !== link.link_label) {
            onRename(id, editValue.trim());
        }
        setIsEditing(false);
    };

    const handleCancelEdit = () => {
        setEditValue(link.link_label);
        setIsEditing(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSaveEdit();
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-start gap-2 p-3 bg-white border border-gray-300 rounded-md hover:bg-gray-50 ${
                isDragging ? 'shadow-lg' : ''
            }`}
        >
                {/* Drag Handle - moved inside card on left */}
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 w-6 flex justify-center pt-0.5"
                    title="Drag to reorder"
                >
                    <GripVertical size={16} />
                </div>

                {/* Links Label Column - Slimmer with wrapping */}
                <div className="w-32">
                    {isEditing ? (
                        <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            onBlur={handleCancelEdit}
                            className="w-full p-2 border rounded-md text-sm border-blue-300"
                            autoFocus
                        />
                    ) : (
                        <span className="text-sm text-gray-700 break-words">{link.link_label || ''}</span>
                    )}
                </div>

                {/* Key Column - Fixed Width */}
                <div className="w-32 pt-1">
                    <span className="text-sm font-mono text-gray-700 break-words">{link.link_key || ''}</span>
                </div>

                {/* Active Column */}
                <div className="w-16 text-center pt-1">
                    <input
                        type="checkbox"
                        checked={!!link.is_active}
                        onChange={(e) => onToggleActive(id, e.target.checked)}
                        className="rounded"
                    />
                </div>

                {/* Required Column */}
                <div className="w-20 text-center pt-1">
                    <input
                        type="checkbox"
                        checked={!!link.is_required}
                        onChange={(e) => onToggleRequired(id, e.target.checked)}
                        className="rounded"
                    />
                </div>

                {/* Actions Column */}
                <div className="w-20 flex gap-1 pt-0.5">
                    {isEditing ? (
                        <>
                            <button
                                onMouseDown={handleSaveEdit}
                                className="p-2 text-green-600 hover:text-green-800"
                                title="Save changes"
                            >
                                <Save size={16} />
                            </button>
                            <button
                                onMouseDown={handleCancelEdit}
                                className="p-2 text-gray-600 hover:text-gray-800"
                                title="Cancel edit"
                            >
                                <X size={16} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="p-2 text-gray-500 hover:text-gray-700"
                                title="Edit field"
                            >
                                <Edit size={16} />
                            </button>
                            <button
                                onClick={() => onDelete(id)}
                                className="p-2 text-gray-500 hover:text-gray-700"
                                title="Delete"
                            >
                                <Trash2 size={16} />
                            </button>
                        </>
                    )}
                </div>
        </div>
    );
}

const SortableLinksList = ({
    data,
    title,
    onDelete,
    onReorder,
    onAdd,
    onRename,
    onToggleActive,
    onToggleRequired
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const sortedData = [...(data || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = sortedData.findIndex(item => item.id === active.id);
            const newIndex = sortedData.findIndex(item => item.id === over.id);
            const newOrder = arrayMove(sortedData, oldIndex, newIndex);
            const orderedIds = newOrder.map(item => item.id);
            onReorder(orderedIds);
        }
    }

    return (
        <div>
            {/* Column Headers */}
            <div className="flex items-center gap-2 p-2 text-sm font-medium text-gray-700 border-b border-gray-300 mb-3">
                <div className="w-6"></div> {/* Space for drag handle, no text */}
                <div className="w-32">Links Label</div>
                <div className="w-32">Key</div>
                <div className="w-16 text-center">Active</div>
                <div className="w-20 text-center">Required</div>
                <div className="w-20 text-center">Actions</div>
            </div>

            {sortedData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No {title.toLowerCase()} links configured by admin.</p>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={sortedData.map(item => item.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                            {sortedData.map(link => (
                                <SortableLinkItem
                                    key={link.id}
                                    id={link.id}
                                    link={link}
                                    onDelete={onDelete}
                                    onRename={onRename}
                                    onToggleActive={onToggleActive}
                                    onToggleRequired={onToggleRequired}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            <button
                onClick={onAdd}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
            >
                <Plus size={16} /> Add {title} Link
            </button>
        </div>
    );
};

export default SortableLinksList;